import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, db } from '../firebase/firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { loadCoverLetterTemplates, loadCoverLetterTemplateHTML } from '../services/templateService';
import { renderTemplate } from '../utils/templateRenderer';
import { getSampleCoverLetterData } from '../utils/sampleCoverLetterData';
import { buildCoverLetterTemplateData } from '../utils/coverLetterDataMapper';
import { sanitizeTemplateHTML } from '../utils/templateSanitizer';
import { getCachedTemplates, cacheRenderedTemplates } from '../utils/templateCache';
import { customToast } from '../utils/toastUtils';
import TemplateSkeletonCard from '../components/TemplateSkeletonCard';
import LazyTemplateCard from '../components/LazyTemplateCard';
import '../components/TemplateSkeletonCard.css';
import './CoverLetterTemplatesGallery.css';

const CoverLetterTemplatesGallery = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [templates, setTemplates] = useState([]);
  const [renderedTemplates, setRenderedTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userPlan, setUserPlan] = useState(null);
  const [authResolved, setAuthResolved] = useState(false);

  // Fetch user plan from Firestore using onAuthStateChanged to handle page refresh
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        if (user) {
          const userDocRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const planData = {
              plan: userData.planType || 'free',
              isActive: userData.isActivePlan || false
            };
            setUserPlan(planData);
          } else {
            setUserPlan({ plan: 'free', isActive: false });
          }
        } else {
          setUserPlan({ plan: 'free', isActive: false });
        }
      } catch (error) {
        console.error('Error fetching user plan:', error);
        setUserPlan({ plan: 'free', isActive: false });
      } finally {
        setAuthResolved(true);
      }
    });

    return () => unsubscribe();
  }, []);

  // Show toast messages when returning from pricing page after successful payment
  useEffect(() => {
    if (location.state?.showSuccess && location.state?.message) {
      customToast.success(location.state.message, { autoClose: 8000 });
    }
    if (location.state?.draftSavedToMyFiles) {
      setTimeout(() => {
        customToast.info('📁 Your edited cover letter has been saved. You can re-select a template and download after payment!', { autoClose: 10000 });
      }, 2500);
    }
    if (location.state?.showSuccess || location.state?.draftSavedToMyFiles) {
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Ref for render cancellation on unmount
  const renderAbortRef = useRef(false);

  useEffect(() => {
    const fetchCoverLetterTemplates = async () => {
      try {
        setLoading(true);
        setError(null);
        renderAbortRef.current = false;
        
        // Load cover letter templates using the service
        const templatesData = await loadCoverLetterTemplates();

        if (templatesData.length === 0) {
          setError('No cover letter templates found. Please ensure cover letter templates are available.');
          return;
        }
        
        // Shuffle templates for mixed display
        const shuffled = shuffleArray(templatesData);
        setTemplates(shuffled);

        // Try to restore from IndexedDB cache
        const templateIds = shuffled.map(t => t.id);
        let cached = {};
        try {
          cached = await getCachedTemplates(templateIds);
        } catch (cacheErr) {
          // Cache unavailable, continue without it
        }
        const cachedCount = Object.keys(cached).length;
        
        if (cachedCount > 0) {
          setRenderedTemplates(cached);
        }

        // Render only uncached templates
        const uncachedTemplates = shuffled.filter(t => !cached[t.id]);
        
        if (uncachedTemplates.length > 0) {
          renderCLTemplatesInBatches(uncachedTemplates);
        }
      } catch (err) {
        console.error('Error fetching cover letter templates:', err);
        setError(`Failed to load cover letter templates: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchCoverLetterTemplates();
    
    return () => {
      renderAbortRef.current = true;
    };
  }, []);

  /**
   * Render cover letter templates in small batches
   */
  const renderCLTemplatesInBatches = async (uncachedTemplates) => {
    const BATCH_SIZE = 4;
    
    for (let i = 0; i < uncachedTemplates.length; i += BATCH_SIZE) {
      if (renderAbortRef.current) break;
      
      const batch = uncachedTemplates.slice(i, i + BATCH_SIZE);
      const results = await Promise.allSettled(
        batch.map(template => renderSingleCLTemplate(template))
      );
      
      const batchRendered = {};
      results.forEach((result, idx) => {
        const templateId = batch[idx].id;
        if (result.status === 'fulfilled' && result.value) {
          batchRendered[templateId] = result.value;
        } else {
          batchRendered[templateId] = null;
        }
      });
      
      setRenderedTemplates(prev => ({ ...prev, ...batchRendered }));
      
      // Cache successful renders
      const successfulRenders = {};
      Object.entries(batchRendered).forEach(([id, html]) => {
        if (html) successfulRenders[id] = html;
      });
      if (Object.keys(successfulRenders).length > 0) {
        cacheRenderedTemplates(successfulRenders).catch(() => {});
      }
      
      // Yield to main thread
      await new Promise(resolve => setTimeout(resolve, 30));
    }
  };

  /**
   * Render a single cover letter template
   */
  const renderSingleCLTemplate = async (template) => {
    let templateHTML;
    
    // Use centralized template service — handles local vs Firebase
    const storagePath = template.storagePath || 
      `templates/cover-letter/${template.plan}/${template.filename}`;
    templateHTML = await loadCoverLetterTemplateHTML(storagePath);

    if (!templateHTML || templateHTML.length < 50) {
      throw new Error('Template HTML too short or empty');
    }

    const sanitizedHTML = sanitizeTemplateHTML(templateHTML);

    // Get full sample data and enrich it with all legacy template aliases
    // so both new (schema A) and legacy (schema B) templates render correctly
    const baseSample = getSampleCoverLetterData();
    const sampleData = buildCoverLetterTemplateData(baseSample);

    const renderedHTML = renderTemplate(sanitizedHTML, sampleData);
    
    if (!renderedHTML || renderedHTML.length < 100) {
      throw new Error('Rendered HTML too short');
    }
    
    return renderedHTML;
  };

  // Shuffle array helper function
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Check if user has access to a template based on their plan
  const hasAccessToTemplate = (templatePlan) => {
    if (!userPlan) {
      // Not logged in - show all as accessible (they'll need to login anyway)
      return true;
    }

    // Free users (no active plan) - all templates accessible for preview
    if (!userPlan.isActive) {
      return true;
    }

    // For active paid users, check plan hierarchy
    const userPlanType = userPlan.plan?.toLowerCase();
    const templateTier = templatePlan.toLowerCase();

    const planHierarchy = {
      'silver': 1,
      'gold': 2,
      'platinum': 3
    };

    const userLevel = planHierarchy[userPlanType] || 1;
    const templateLevel = planHierarchy[templateTier] || 1;

    return userLevel >= templateLevel;
  };

  // Check if template should show crown icon (locked)
  const isTemplateLocked = (templatePlan) => {
    // Auth not resolved yet — treat as locked to prevent flash of wrong state
    if (!authResolved) {
      return true;
    }
    // ALL templates are locked for users without an active paid plan
    if (!userPlan || !userPlan.isActive) {
      return true;
    }
    // For active paid users, check if they have access to this template tier
    return !hasAccessToTemplate(templatePlan);
  };

  const handleTemplateSelect = (template) => {
    // Check if template is locked
    if (isTemplateLocked(template.plan)) {
      navigate('/pricing', {
        state: {
          message: 'Upgrade your plan to access cover letter templates and start creating professional cover letters.',
          returnUrl: '/cover-letter-templates'
        }
      });
      return;
    }

    // Navigate to cover letter editor with selected template
    // Pass minimal data (no functions) that can be serialized
    navigate('/cover-letter-editor', { 
      state: { 
        selectedTemplate: {
          id: template.id,
          name: template.name,
          plan: template.plan,
          filename: template.filename,
          isLocal: template.isLocal,
          downloadURL: template.downloadURL,
          storagePath: template.storagePath
        }
      } 
    });
  };

  // Loading state
  if (loading) {
    return (
      <div className="cl-templates-loading-screen">
        <div className="cl-loading-animation">
          <div className="cl-spinner-container">
            <div className="cl-spinner-ring"></div>
            <div className="cl-spinner-ring"></div>
            <div className="cl-spinner-ring"></div>
            <div className="cl-spinner-logo">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
          <h2 className="cl-loading-title">Loading Cover Letter Templates</h2>
          <div className="cl-loading-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="cl-templates-gallery">
        <div className="cl-templates-hero">
          <div className="cl-hero-content">
            <h1 className="cl-hero-title">Error Loading Templates</h1>
            <p className="cl-hero-subtitle">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cl-templates-gallery">
      {/* Hero Section */}
      <div className="cl-templates-hero">
        <div className="cl-hero-content">
          <h1 className="cl-hero-title">Choose Your Cover Letter Template</h1>
          <p className="cl-hero-subtitle">
            Select a professional cover letter template that matches your style and industry
          </p>
          <div className="cl-badge-container">
            <div className="cl-badge-item">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>Professional Designs</span>
            </div>
            <div className="cl-badge-item">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13 10V3L4 14h7v7l9-11h-7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>Quick Customization</span>
            </div>
            <div className="cl-badge-item">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>Industry Standard</span>
            </div>
          </div>
        </div>
      </div>

      {/* Templates Grid */}
      <div className="cl-templates-container">
        <div className="cl-templates-grid">
          {templates.map((template) => {
            const locked = isTemplateLocked(template.plan);
            
            return (
              <div 
                key={template.id} 
                className={`cl-template-card ${locked ? 'locked' : ''}`}
                title={`Template: ${template.id}`}
              >
                <div className="cl-template-preview-wrapper">
                  {/* Plan Badge - Shows on ALL templates, expands on hover with tier name */}
                  <div className={`cl-tier-badge cl-tier-${template.plan.toLowerCase()}`}>
                    <span className="cl-badge-icon">
                      {template.plan.toLowerCase() === 'silver' && (
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="16" height="16">
                          <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.2"/>
                          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor"/>
                        </svg>
                      )}
                      {template.plan.toLowerCase() === 'gold' && (
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="16" height="16">
                          <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.2"/>
                          <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm14 3c0 .6-.4 1-1 1H6c-.6 0-1-.4-1-1v-1h14v1z" fill="currentColor"/>
                        </svg>
                      )}
                      {template.plan.toLowerCase() === 'platinum' && (
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="16" height="16">
                          <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.2"/>
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="currentColor"/>
                          <circle cx="12" cy="8" r="1.5" fill="currentColor"/>
                          <circle cx="8" cy="10" r="1" fill="currentColor"/>
                          <circle cx="16" cy="10" r="1" fill="currentColor"/>
                        </svg>
                      )}
                    </span>
                    <span className="cl-badge-label">{template.plan.charAt(0).toUpperCase() + template.plan.slice(1)}</span>
                  </div>
                  
                  {renderedTemplates[template.id] ? (
                    <LazyTemplateCard
                      renderedHTML={renderedTemplates[template.id]}
                      templateName={template.name}
                    />
                  ) : renderedTemplates[template.id] === null ? (
                    <div className="cl-template-preview-error">
                      <div className="cl-error-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"/>
                          <line x1="12" y1="8" x2="12" y2="12"/>
                          <line x1="12" y1="16" x2="12.01" y2="16"/>
                        </svg>
                      </div>
                      <p>Preview unavailable</p>
                      <small>Template will work in editor</small>
                    </div>
                  ) : (
                    <TemplateSkeletonCard />
                  )}
                  
                  <div className="cl-template-overlay">
                    <button 
                      className={`cl-use-template-btn ${locked ? 'locked-btn' : ''}`}
                      onClick={() => handleTemplateSelect(template)}
                    >
                      {locked ? (
                        <>
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="20" height="20" style={{marginRight: '8px'}}>
                            <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" fill="white"/>
                          </svg>
                          Upgrade to Use
                        </>
                      ) : (
                        'Choose Template'
                      )}
                    </button>
                  </div>
                </div>
                <div className="cl-template-info">
                  <span className={`cl-plan-badge cl-plan-${template.plan}`}>
                    {template.plan}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CoverLetterTemplatesGallery;
